import { useMemo, useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import {
  useGetProjectsQuery,
  useGetProjectStatesQuery,
  useGetTeamsQuery,
  useGetItemsQuery,
  usePatchContactMutation,
} from 'store/api';
import {
  BadgesCell,
  DateChipCell,
  ColorTypographyCell,
  MailMenuCell,
  RowCell,
  TabIndexTable,
  BackdropLoading,
  AvatarsCell,
  WARNING_DAYS,
  formattedDate,
} from '@centerpiece/components';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { FoodBank } from '@mui/icons-material';
import { Team, ProjectStage, Contact, ProjectState } from 'store/api/models';
import { ItemStatus } from 'utils/status.utils';
import { useTutorialContext } from 'tutorials/tutorial.context';
import {
  useItemsAtRisk,
  useProjectsIndex,
  useItemCounts,
  useProjectsMsgs,
  useProjectsCmts,
} from 'utils/index.utils';
import { useIndexContext } from 'contexts/index.context';
import { ProjectTabDrawer } from 'drawers/ProjectTabDrawer';

export const InactiveProjectsIndexPage = () => {
  const navigate = useNavigate();
  const { contactRecord, cmts, msgs, refetchCount, refetchIndex } =
    useIndexContext();

  // BEGIN USESTATE
  const [projectIndex, setProjectIndex] = useState<{ [key: number]: any }>({});
  const indexProjects = useMemo(
    () => Object.values(projectIndex).filter(i => i.id),
    [projectIndex]
  );
  const [itemIndex, setItemIndex] = useState<{ [key: number]: any }>({});
  const toDoSummaries = useMemo(() => {
    return Object.values(itemIndex).filter(i => i.id);
  }, [itemIndex]);

  const [ownershipFilter, setOwnershipFilter] = useState<Team>({
    id: -1,
    name: 'My Projects',
  } as Team);
  const [project, setProject] = useState<number>(0);
  const [filter, setFilter] = useState<ProjectState>({
    id: 1,
    name: 'Complete',
  } as ProjectStage);

  /////// BEGIN QUERIES
  const {
    data: projects = [],
    isLoading: projectListLoading,
    isFetching: projectListFetching,
    refetch: refetchProjects,
  } = useGetProjectsQuery({
    params: {
      optimized: 'true',
      archived: filter?.id === -1 ? true : undefined,
      state: filter?.id > 0 ? filter?.id : undefined,
      contact_id: ownershipFilter.id === -1 ? contactRecord.id : undefined,
      team_ids: ownershipFilter.id > 0 ? ownershipFilter.id : undefined,
      template: false,
    },
  });
  const { data: riskItems = [] } = useGetItemsQuery({
    params: {
      optimized: 'projectindex',
    },
  });
  const { data: itemCounts = [] } = useGetItemsQuery({
    params: {
      optimized: 'item_counts',
    },
  });
  const { data: states = [] } = useGetProjectStatesQuery({});
  const { data: teams = [] } = useGetTeamsQuery({});
  /////// END QUERIES

  useEffect(() => refetchProjects(), [refetchCount]);

  const [patchContact, patchContactResult] = usePatchContactMutation();
  const { state, setState } = useTutorialContext();
  const location = useLocation();
  const initialTab = () => {
    let tab = Number(location.hash.match(/at=(\d)/)?.[1]) || 0;
    window.history.replaceState(null, '', location.pathname);
    return tab;
  };
  useEffect(() => {
    if (state.tourActive) {
      setState(currState => ({
        ...currState,
        run: true,
      }));
    }
    // eslint-disable-next-line
  }, []);
  useProjectsIndex({ projects, setProjectIndex });
  useItemsAtRisk({ riskItems, setProjectIndex });
  useItemCounts({ itemCounts, setProjectIndex });
  useProjectsMsgs({ msgs, setProjectIndex });
  useProjectsCmts({ cmts, setProjectIndex });
  useEffect(() => {
    if (contactRecord?.form?.authorization === 'Executive') {
      setOwnershipFilter({ id: -2, name: 'All Projects' } as any);
    } else if (
      contactRecord?.form?.authorization === 'Team Leader' &&
      contactRecord?.form?.team
    ) {
      setOwnershipFilter(contactRecord?.form?.team as any);
    }
    // eslint-disable-next-line
  }, [contactRecord]);

  const tableprops: any = useMemo(
    () => ({
      exportFormat: (rows: RowCell[]) => {
        const formattedData: any = rows?.map(row => ({
          'Project Name': row.title.text,
          Financials: row.financials,
          Teams: row.teams?.reduce((acc: string, obj: any) => {
            if (acc && acc !== '') {
              return acc + `, ${obj.name}`;
            } else {
              return `${obj.name} `;
            }
          }, ''),
          'Project Members': row.team?.data?.reduce((acc: string, obj: any) => {
            if (acc && acc !== '') {
              return acc + `, ${obj.firstname} ${obj.lastname}`;
            } else {
              return `${obj.firstname} ${obj.lastname}`;
            }
          }, ''),
          Stage: row.stage_due?.title,
          'Stage Due Date':
            row.stage_due?.due && formattedDate(row.stage_due?.due),
          'Next Item': row.next_item_due?.title,
          'Next Item Due Date':
            row.next_item_due?.due && formattedDate(row.next_item_due?.due),
          'Project Completion': row.due_date && formattedDate(row.due_date),
          'Project Location': row.title.tooltip,
        }));
        return [formattedData, `${ownershipFilter.name}`];
      },
      filters:
        contactRecord?.form?.authorization === 'Executive' ||
        contactRecord?.form?.authorization === 'Organization Member' ||
        ((contactRecord?.form?.authorization === 'Team Leader' ||
          contactRecord?.form?.authorization === 'Team Member') &&
          contactRecord?.form?.team)
          ? [
              {
                options:
                  (contactRecord?.form?.authorization === 'Team Leader' ||
                    contactRecord?.form?.authorization === 'Team Member') &&
                  contactRecord?.form?.team
                    ? [
                        { id: -1, name: 'My Projects' },
                        teams.find(t => t.id == contactRecord?.form?.team?.id),
                      ]
                    : [
                        { id: -1, name: 'My Projects' },
                        { id: -2, name: 'All Projects' },
                        ...(teams || []),
                      ],
                label: 'Project Relationship',
                defaultOption: ownershipFilter,
                onChange: (event: any, newValue: any) =>
                  setOwnershipFilter(newValue),
                loading: projectListFetching,
              },
            ]
          : undefined,
      // checkboxes: true,
      // icons: true,
      // iconHead: <Typography variant='subtitle2'>Type</Typography>,
      actions: [
        // {
        //   name: 'Create Kitchen Project',
        //   icon: <FoodBank />,
        //   onClick: () => setOpenDialog('FES'),
        // },
        // {
        //   name: 'Create Custom Project',
        //   icon: <WorkOutlined />,
        //   onClick: () => setOpenDialog('Custom'),
        // },
      ],
      onRowClick: (event: any, row: any) => navigate(`project/${row.id}`),
      // isLoading: projectListLoading,
      defaultOrder: 'desc',
      defaultOrderBy: 'stage_due.due',
      hoverCell: true,
      headCells: [
        // {
        //   name: 'icon',
        //   icon: true,
        //   label: 'Class',
        // },
        {
          name: 'title',
          label: 'Project',
          textFilter: 'title.text',
          sort: 'title.text',
          component: ColorTypographyCell,
        },
        {
          name: 'financials',
          label: 'Financials',
          sort: 'financials.sort',
          component: ColorTypographyCell,
        },
        // {
        //   name: 'type_name',
        //   label: 'Type',
        //   checkboxFilter: true,
        // },
        {
          name: 'team',
          checkboxFilter: 'team.team',
          label: 'Team',
          component: AvatarsCell,
        },
        {
          name: 'stage_due',
          label: 'Stage (Due)',
          sort: 'stage_due.due',
          component: DateChipCell,
        },
        // {
        //   name: 'due',
        //   label: 'Stage Due Date',
        //   component: DateChipCell,
        // },
        // {
        //   name: 'next_item',
        //   label: 'Next Item',
        // },
        {
          name: 'next_item_due',
          sort: 'next_item_due.due',
          label: 'Next Item (Due)',
          component: DateChipCell,
        },
        {
          name: 'due_date',
          label: 'Project Completion',
          sort: 'due_date',
          component: ColorTypographyCell,
          render: (row: any, name: any): any => {
            const value = row[name];
            if (value && value !== '') {
              let days = moment(value).diff(moment.now(), 'days');
              return {
                text: moment(value).format('MMM D YYYY'),
                color:
                  days < WARNING_DAYS
                    ? days < 0
                      ? 'error'
                      : 'warning'
                    : 'info',
              };
            } else {
              return { text: '', color: 'info' };
            }
          },
        },
        {
          name: 'risk',
          label: 'Items at Risk',
          hover: true,
          component: BadgesCell,
        },
        {
          name: 'msgs',
          label: '',
          clearFilter: true,
          align: 'right',
          disablePadding: true,
          component: MailMenuCell,
          render: (row: any, name: any): any => {
            return {
              msgs: row.msgs,
              counts: row.counts,
              cmts: row.cmts,
              // ...(row[name] || {}),
              infoClick: () => setProject(row.id),
            };
          },
        },
      ],
    }),
    [ownershipFilter, projectListFetching, contactRecord]
  );

  return (
    <>
      <ProjectTabDrawer
        open={project > 0}
        initialTab={'preview'}
        previewTab={true}
        id={project}
        onClose={() => {
          setProject(0);
        }}
        afterSave={() => {
          refetchIndex();
        }}
      />
      <Box
        id={'tab-index-table'}
        maxWidth={'xl'}
        sx={{ pt: { xs: 4, md: 0 }, pb: { xs: 15, md: 10 } }}
      >
        {projectListLoading && <BackdropLoading />}
        <TabIndexTable
          filterTitle="Inactive Projects"
          accordian={true} //calendar accordian at the bottome
          initialTab={initialTab()}
          tables={[
            {
              title: `${filter.name} projects`,
              key: 'inactive',
              visible: true,
              ...{
                ...tableprops,
                filters: [
                  {
                    options: [
                      // { id: 3, name: 'Active' },
                      ...states.filter(s => s?.subscription),
                      { id: 1, name: 'Complete' },
                      { id: 2, name: 'Canceled' },
                      { id: 4, name: 'Lost' },
                      { id: -1, name: 'Archived' },
                    ],
                    label: 'Project State',
                    defaultOption: filter,
                    onChange: (event: any, newValue: any) =>
                      setFilter(newValue),
                    loading: projectListFetching,
                  },
                ],
              },
              rows: indexProjects || ([] as RowCell[]),
            },
          ]}
        />
      </Box>
    </>
  );
};
